import React from "react";
import { Buttons } from "src/components/Buttons/Buttons.component";
import { cn } from "@danishagro/shared/src/helpers/classNames.helper";
import { DA_Container } from "@danishagro/shared/src/components/atoms/Container/Container.component";
import { DA_Text } from "@danishagro/shared/src/components/atoms/Text/Text.component";
import {
    DA_Title,
    DA_TitleTag,
} from "@danishagro/shared/src/components/atoms/Title/Title.component";
import { ImageViewer } from "@danishagro/cms/src/components/ImageViewer/ImageViewer.component";
import { useBackgroundColor } from "@danishagro/cms/src/blocks/hooks/useBackgroundColor/useBackgroundColor.hook";
import { usePadding } from "@danishagro/cms/src/blocks/hooks/usePadding/usePadding.hook";
import Form from "./components/Form/Form.component";
import { TextFormProps } from "./TextForm.interface";
import { usePosition } from "./hooks/usePosition.hook";
import { useBackgroundCoverImage } from "./hooks/useBackgroundCoverImage";
import S from "./TextForm.module.scss";

export const TextForm = (props: TextFormProps) => {
    const hasAnyTextContent =
        props.image || props.tagline || props.headline || props.text || props.buttons?.[0]
            ? true
            : false;

    const { backgroundColorClass, alternativeButtonTheme, hasBackgroundColor } = useBackgroundColor(
        props.backgroundColor
    );
    const { backgroundCoverImage } = useBackgroundCoverImage(
        props.backgroundCover,
        hasAnyTextContent
    );
    const { positionClass } = usePosition(props.position, S);
    const { paddingClass } = usePadding(props.isCompact, hasBackgroundColor);

    return (
        <div className={cn(S.wrapper, paddingClass, backgroundColorClass, positionClass)}>
            <DA_Container noVerticalPadding>
                {/** Content */}

                <div className={S.innerWrapper}>
                    {/** Background cover image */}
                    {backgroundCoverImage ? (
                        <ImageViewer
                            className={S.coverImage}
                            src={backgroundCoverImage}
                            aspectRatioX={16}
                            aspectRatioY={9}
                        />
                    ) : null}

                    {hasAnyTextContent ? (
                        <div className={cn(S.textWrapper, hasAnyTextContent && S.halfWidth)}>
                            {/** Image */}
                            {props.image ? (
                                <ImageViewer
                                    className={S.image}
                                    src={props.image.url}
                                    aspectRatioX={props.image.width}
                                    aspectRatioY={props.image.height}
                                />
                            ) : null}

                            {/** Tagline */}
                            {props.tagline ? (
                                <span className={S.tagline}>{props.tagline}</span>
                            ) : null}

                            {/** Title */}
                            {props.headline ? (
                                <DA_Title h2 tag={DA_TitleTag.H2} noMargin fullWidth>
                                    {props.headline}
                                </DA_Title>
                            ) : null}

                            {/** Text */}
                            {props.text ? (
                                <div className={S.text}>
                                    <DA_Text html={props.text} noPadding />
                                </div>
                            ) : null}

                            {/** Buttons */}
                            {props.buttons ? (
                                <Buttons buttons={props.buttons} theme={alternativeButtonTheme} />
                            ) : null}
                        </div>
                    ) : null}

                    {/** Form */}
                    {props.form?.formData?.allFieldSets ? (
                        <div
                            className={cn(
                                S.formWrapper,
                                hasAnyTextContent && !props.position && S.positionFormRight
                            )}
                        >
                            {props.form?.formData?.allFieldSets && <Form {...props.form} />}
                        </div>
                    ) : null}
                </div>
            </DA_Container>
        </div>
    );
};

export default TextForm;
